import React, {useState} from 'react';
import type {FetchVacancyResponse} from '@rocket-ui/lk-api';
import {getOfferCustomerInitials} from 'features/offers/model/offerCustomerInitials';
import {
	formatOfferLocationLabel,
	shouldShowOfferAutoApplyCheckbox,
} from 'features/offers/model/offerAutoApply';
import {useOfferApplyAutoReplyFlow} from 'features/offers/ui/OfferApplyAutoReplyFlow';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {ProfileAlertsBanner} from 'features/profile';
import {Button} from 'ui';
import {OfferAppliedBar} from './OfferAppliedBar';
import {OfferBulletList} from './OfferBulletList';
import {OfferMobileHero} from './OfferMobileHero';
import {OfferPageHeader} from './OfferPageHeader';
import {OfferSection} from './OfferSection';
import {OfferServicesList} from './OfferServicesList';
import styles from './OfferPage.module.css';

export type OfferProps = {
	vacancy: FetchVacancyResponse;
};

function formatRublesFromCents(cents?: number | null): string {
	if (cents == null) return '—';
	return `${(cents / 100).toLocaleString('ru-RU')} ₽`;
}

function formatOfferDate(date?: string | null): string | null {
	if (!date) return null;

	const parsedDate = new Date(date);
	if (Number.isNaN(parsedDate.getTime())) return null;

	return new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	})
		.format(parsedDate)
		.replace(/\s*г\.?\s*$/u, '');
}

export function Offer({vacancy}: OfferProps): React.ReactElement {
	const isMobile = useIsMaxSm();
	const {applyWithFlow, isApplying, flow} = useOfferApplyAutoReplyFlow();
	const [skipAutoApply, setSkipAutoApply] = useState(false);
	const title = vacancy.name?.trim() || 'Без названия';
	const price = formatRublesFromCents(vacancy.reward?.amountCents);
	const location = formatOfferLocationLabel(vacancy);
	const periodDate = formatOfferDate(vacancy.periodTo);
	const publishedDate = formatOfferDate(vacancy.publishedAt);
	const metaDate = periodDate ?? publishedDate;
	const services = vacancy.services ?? [];
	const requirements = vacancy.requirements?.filter(Boolean) ?? [];
	const conditions = vacancy.conditions?.filter(Boolean) ?? [];
	const customerName = vacancy.customer?.name?.trim() || 'Не указан';
	const showMobileApply = isMobile && !vacancy.applied && vacancy.id != null;
	const showAutoApplyCheckbox = shouldShowOfferAutoApplyCheckbox(vacancy);

	const handleApply = (options?: {skipAutoApply?: boolean}) => {
		void applyWithFlow(vacancy, options);
	};

	return (
		<>
			{isMobile ? (
				<OfferMobileHero vacancy={vacancy} title={title} price={price} />
			) : (
				<OfferPageHeader
					vacancy={vacancy}
					title={title}
					price={price}
					isApplying={isApplying}
					onApply={handleApply}
				/>
			)}

			{vacancy.applied && <OfferAppliedBar appliedAt={vacancy.appliedAt} />}

			<div className={styles.page}>
				<ProfileAlertsBanner />
				{(location || metaDate) && (
					<div className={styles.meta}>
						{location && <p className={styles.metaLocation}>{location}</p>}
						{metaDate && <p className={styles.metaDate}>{metaDate}</p>}
					</div>
				)}

				{vacancy.description?.trim() && (
					<OfferSection title="Чем предстоит заниматься?" titleClassName={styles.sectionTitle}>
						<p className={styles.description}>{vacancy.description.trim()}</p>
					</OfferSection>
				)}

				{services.length > 0 && (
					<OfferSection title="Услуги" titleClassName={styles.sectionTitle}>
						<OfferServicesList services={services} />
					</OfferSection>
				)}

				{requirements.length > 0 && (
					<OfferSection title="Требования" titleClassName={styles.sectionTitle}>
						<OfferBulletList items={requirements} />
					</OfferSection>
				)}

				{conditions.length > 0 && (
					<OfferSection title="Условия" titleClassName={styles.sectionTitle}>
						<OfferBulletList items={conditions} />
					</OfferSection>
				)}

				{periodDate && (
					<OfferSection title="Срок выполнения" titleClassName={styles.sectionTitle}>
						<p className={styles.deadlineValue}>До {periodDate}</p>
					</OfferSection>
				)}

				<OfferSection title="Заказчик" titleClassName={styles.sectionTitle}>
					<div className={styles.customerRow}>
						<div className={styles.customerLogo}>{getOfferCustomerInitials(customerName) || '—'}</div>
						<span className="text-base">{customerName}</span>
					</div>
				</OfferSection>

				{showMobileApply && (
					<div className={styles.mobileApply}>
						{showAutoApplyCheckbox && (
							<label className={styles.autoApplyLabel}>
								<span className={styles.autoApplyCheckboxWrap}>
									<input
										type="checkbox"
										className={styles.autoApplyCheckbox}
										checked={skipAutoApply}
										onChange={(event) => setSkipAutoApply(event.target.checked)}
									/>
									<span className={styles.autoApplyCheckboxBox} aria-hidden="true" />
								</span>
								<span>Не хочу откликаться автоматически</span>
							</label>
						)}
						<Button
							type="button"
							className={styles.applyButton}
							disabled={isApplying}
							onClick={() => handleApply({skipAutoApply: showAutoApplyCheckbox ? skipAutoApply : false})}
						>
							Откликнуться
						</Button>
					</div>
				)}
			</div>

			{flow}
		</>
	);
}
